// Tripkitty web app: directory + per-trip views. (06-web-ui.md)
import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import { LocationProvider, Router, Route, useLocation } from "preact-iso";
import {
  api,
  captureToken,
  type Balance,
  type Expense,
  type Member,
  type Settlement,
  type TripCard,
  type TripEvent,
  type TripState,
} from "./api";
import {
  Avatars,
  BalanceStrip,
  CategoryIcon,
  EmptyState,
  MoneyAmount,
  SettlementList,
  StatusPill,
  fmt,
} from "./components";

function Masthead() {
  return (
    <header class="masthead">
      <div class="brand">
        <a href="/app">Roamsplit</a>
      </div>
      <div class="tag">honest receipts for shared trips</div>
    </header>
  );
}

function useAsync<T>(fn: () => Promise<T>, deps: any[] = []): { data?: T; error?: string; loading: boolean } {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({ loading: true });
  useEffect(() => {
    let live = true;
    setState({ loading: true });
    fn()
      .then((data) => live && setState({ data, loading: false }))
      .catch((e) => live && setState({ error: String(e), loading: false }));
    return () => {
      live = false;
    };
    // eslint-disable-next-line
  }, deps);
  return state;
}

// ---------------------------------------------------------------- Directory
function Directory() {
  const { data, error, loading } = useAsync(() => api.trips());
  if (loading) return <div class="loading">Loading your trips…</div>;
  if (error) return <EmptyState title="Couldn't load" body="Open this page from your Telegram group via /web." />;
  const trips = data ?? [];
  if (trips.length === 0)
    return <EmptyState title="No trips yet" body="Start one in your Telegram group with /newtrip." />;
  return (
    <div>
      <h1 class="trip-name">Your trips</h1>
      <div class="rows">
        {trips.map((t: TripCard) => (
          <a class="tripcard" href={`/trip/${t.id}`}>
            <div class="grow">
              <div style="font-family:var(--serif);font-weight:600;font-size:18px">
                {t.name} <StatusPill status={t.status} />
              </div>
              <div class="meta sub">
                {t.memberCount} {t.memberCount === 1 ? "member" : "members"} · {t.baseCurrency}
              </div>
            </div>
            <MoneyAmount minor={t.myNetMinor} ccy={t.baseCurrency} signed />
          </a>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Trip tabs
function Tabs({ id, on }: { id: string; on: string }) {
  const tab = (key: string, label: string, href: string) => (
    <a class={on === key ? "on" : ""} href={href}>
      {label}
    </a>
  );
  return (
    <nav class="tabs">
      {tab("ledger", "Ledger", `/trip/${id}`)}
      {tab("history", "Audit", `/trip/${id}/history`)}
      {tab("report", "Report", `/trip/${id}/report`)}
    </nav>
  );
}

function TripHeader({ trip }: { trip: TripState }) {
  return (
    <div>
      <h1 class="trip-name">
        {trip.name} <StatusPill status={trip.status} />
      </h1>
      <p class="sub">Base currency {trip.baseCurrency} · {trip.members.length} members</p>
    </div>
  );
}

// ---------------------------------------------------------------- Trip detail
function TripDetail({ params }: { params: { id: string } }) {
  const id = params.id;
  const trip = useAsync(() => api.trip(id), [id]);
  const ledger = useAsync(() => api.ledger(id, true), [id]);
  const balances = useAsync(() => api.balances(id), [id]);
  const settlement = useAsync(() => api.settlement(id), [id]);

  if (trip.loading) return <div class="loading">Loading…</div>;
  if (trip.error || !trip.data)
    return <EmptyState title="Can't open this trip" body="Open it from Telegram via /web to get an access link." />;

  const t = trip.data;
  const nameOf = (uid: string) => t.members.find((m) => m.userId === uid)?.displayName ?? uid;
  const settled = t.status === "closed";

  return (
    <div>
      <TripHeader trip={t} />
      <Tabs id={id} on="ledger" />

      <BalanceStrip balances={balances.data ?? []} members={t.members} settled={settled} />

      <h2 class="section">{settled ? "Final settlement" : "Settle up"}</h2>
      <SettlementList transfers={settlement.data ?? []} ccy={t.baseCurrency} nameOf={nameOf} closing={settled} />

      <h2 class="section">Ledger</h2>
      <Ledger expenses={ledger.data ?? []} trip={t} nameOf={nameOf} />

      <h2 class="section">Totals</h2>
      <Totals trip={t} />

      <div class="toolbar">
        <a class="btn ghost" href={api.exportUrl(id, "csv")}>Export CSV</a>
        <a class="btn ghost" href={api.exportUrl(id, "json")}>Export JSON</a>
      </div>
    </div>
  );
}

function Ledger({ expenses, trip, nameOf }: { expenses: Expense[]; trip: TripState; nameOf: (id: string) => string }) {
  if (expenses.length === 0)
    return <EmptyState title="No expenses yet" body="Add them from your Telegram group." />;
  return (
    <div class="rows">
      {expenses.map((e) => {
        const showBase = e.currency !== trip.baseCurrency;
        return (
          <a class={`row ${e.voidedAt ? "voided" : ""}`} href={`/trip/${trip.id}/expense/${e.handle}`}>
            <span class="handle">#{e.handle}</span>
            <span class="grow">
              <span class="desc">
                <CategoryIcon category={e.category} /> {e.description}
              </span>
              {e.voidedAt ? <span class="tag-void"> voided</span> : null}
              <div class="meta">paid by {nameOf(e.paidBy)} · {splitLabel(e)}</div>
            </span>
            <span style="text-align:right">
              <MoneyAmount minor={e.currency === trip.baseCurrency ? e.baseAmountMinor : e.amountMinor} ccy={e.currency} />
              {showBase ? <div class="meta money">{fmt(e.baseAmountMinor, trip.baseCurrency)}</div> : null}
            </span>
          </a>
        );
      })}
    </div>
  );
}

function splitLabel(e: Expense): string {
  switch (e.splitType) {
    case "equal":
      return `split equally (${e.shares.length})`;
    case "shares":
      return "by shares";
    case "exact":
      return "exact split";
    case "mixed":
      return "mixed split";
    default:
      return "split";
  }
}

function Totals({ trip }: { trip: TripState }) {
  const cats = Object.entries(trip.totals.byCategory).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      <div class="totals">
        <span class="k">Total spend</span>
        <MoneyAmount minor={trip.totals.spentMinor} ccy={trip.baseCurrency} />
      </div>
      <h2 class="section">By category</h2>
      <div class="totals">
        {cats.map(([cat, minor]) => (
          <>
            <span class="k">
              <CategoryIcon category={cat} /> {cat}
            </span>
            <MoneyAmount minor={minor} ccy={trip.baseCurrency} />
          </>
        ))}
      </div>
      <h2 class="section">Per person</h2>
      <div class="totals">
        {Object.entries(trip.totals.byMember).map(([uid, v]) => (
          <>
            <span class="k">{trip.members.find((m) => m.userId === uid)?.displayName ?? uid}</span>
            <span class="money">
              paid {fmt(v.paidMinor, trip.baseCurrency)} · owed {fmt(v.owedMinor, trip.baseCurrency)}
            </span>
          </>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Expense detail
function ExpenseDetail({ params }: { params: { id: string; handle: string } }) {
  const id = params.id;
  const handle = Number(params.handle);
  const trip = useAsync(() => api.trip(id), [id]);
  const ledger = useAsync(() => api.ledger(id, true), [id]);
  if (trip.loading || ledger.loading) return <div class="loading">Loading…</div>;
  const t = trip.data!;
  const e = (ledger.data ?? []).find((x) => x.handle === handle);
  if (!t || !e) return <EmptyState title="Expense not found" body="It may have been voided." />;
  const nameOf = (uid: string) => t.members.find((m) => m.userId === uid)?.displayName ?? uid;
  return (
    <div>
      <p class="sub"><a href={`/trip/${id}`}>← {t.name}</a></p>
      <h1 class="trip-name">#{e.handle} · {e.description}</h1>
      <p class="sub">
        <CategoryIcon category={e.category} /> {e.category} · paid by {nameOf(e.paidBy)}
        {e.voidedAt ? " · voided" : ""}
      </p>
      <div class="totals">
        <span class="k">Amount</span>
        <MoneyAmount minor={e.amountMinor} ccy={e.currency} />
        {e.currency !== t.baseCurrency ? (
          <>
            <span class="k">In {t.baseCurrency}</span>
            <MoneyAmount minor={e.baseAmountMinor} ccy={t.baseCurrency} />
            <span class="k">Rate</span>
            <span class="money">{e.fxSource} · {e.fxRate.toPrecision(6)}</span>
          </>
        ) : null}
      </div>
      <h2 class="section">Who owes what</h2>
      <div class="rows">
        {e.shares.map((s) => (
          <div class="row">
            <span class="grow">{nameOf(s.userId)}</span>
            <MoneyAmount minor={s.owedBaseMinor} ccy={t.baseCurrency} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Audit
function History({ params }: { params: { id: string } }) {
  const id = params.id;
  const trip = useAsync(() => api.trip(id), [id]);
  const history = useAsync(() => api.history(id), [id]);
  if (trip.loading) return <div class="loading">Loading…</div>;
  const t = trip.data!;
  if (!t) return <EmptyState title="Can't open this trip" body="Open it from Telegram via /web." />;
  const evts = history.data ?? [];
  return (
    <div>
      <TripHeader trip={t} />
      <Tabs id={id} on="history" />
      <h2 class="section">Audit trail</h2>
      {evts.length === 0 ? (
        <EmptyState title="Nothing recorded yet" body="Events appear here as the trip happens." />
      ) : (
        <div>
          {evts.map((ev: TripEvent) => (
            <div class="evt">
              <div>{ev.summary}</div>
              <div class="when">{new Date(ev.createdAt).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Report
function Report({ params }: { params: { id: string } }) {
  const id = params.id;
  const trip = useAsync(() => api.trip(id), [id]);
  const balances = useAsync(() => api.balances(id), [id]);
  if (trip.loading) return <div class="loading">Loading…</div>;
  const t = trip.data!;
  if (!t) return <EmptyState title="Can't open this trip" body="Open it from Telegram via /web." />;
  return (
    <div>
      <TripHeader trip={t} />
      <Tabs id={id} on="report" />
      <Totals trip={t} />
      <h2 class="section">Net position</h2>
      <div class="rows">
        {(balances.data ?? []).map((b: Balance) => (
          <div class="row">
            <span class="grow">{t.members.find((m) => m.userId === b.userId)?.displayName ?? b.userId}</span>
            <MoneyAmount minor={b.netBaseMinor} ccy={t.baseCurrency} signed />
          </div>
        ))}
      </div>
      <div class="toolbar">
        <a class="btn ghost" href={api.exportUrl(id, "csv")}>Export CSV</a>
        <a class="btn ghost" href={api.exportUrl(id, "json")}>Export JSON</a>
      </div>
    </div>
  );
}

function NotFound() {
  return <EmptyState title="Lost the receipt" body="That page isn't here. Go back to your trips." />;
}

function App() {
  return (
    <LocationProvider>
      <div class="wrap">
        <Masthead />
        <Router>
          <Route path="/" component={Directory} />
          <Route path="/app" component={Directory} />
          <Route path="/trip/:id" component={TripDetail} />
          <Route path="/trip/:id/expense/:handle" component={ExpenseDetail} />
          <Route path="/trip/:id/history" component={History} />
          <Route path="/trip/:id/report" component={Report} />
          <Route default component={NotFound} />
        </Router>
      </div>
    </LocationProvider>
  );
}

captureToken();
render(<App />, document.getElementById("app")!);
